// Direct nsec login.
//
// This is the LEAST secure of our three login modes. The nsec lives in
// localStorage and is decoded + finalised in-process for every signed
// event. We never send it to the Worker; the Worker only ever sees signed
// events. The Login Modal carries a security warning before the user can
// type their nsec in.
//
// Why we offer it at all: parity with sibling projects (surf / gold /
// press / TNR) and accessibility for users who do not run a NIP-46 signer
// or a NIP-07 extension. The kind allowlist still applies because all
// signing funnels through `publishToNostr()`.
//
// Security rules enforced here:
//   - Never log or include the nsec / secret-bytes in thrown errors.
//   - `validateNsec` returns `secretHex` for the caller to persist; it is
//     up to the caller (frontend) to store the original nsec1 string in
//     localStorage. The engine never touches localStorage.
//   - `signWithNsec` reconstructs the secret bytes from `secretHex` on
//     every call, signs with `nostr-tools` `finalizeEvent`, and immediately
//     drops the reference. We do not cache the bytes.
//   - The returned NostrSigner enforces post-sign kind invariant: if a
//     caller asks for kind X and we somehow produce kind Y we throw.
//     (`publishToNostr` also re-checks this; defence in depth.)

import { finalizeEvent, getPublicKey } from 'nostr-tools/pure';
import { nip19 } from 'nostr-tools';

import { InvalidNsecError } from './types.js';
import type {
  NostrSigner,
  SignedNostrEvent,
  UnsignedNostrEvent,
} from './types.js';

/** Successful decode of an `nsec1...` bech32 string. */
export interface NsecValidationOk {
  readonly pubkey: string;
  readonly secretHex: string;
}

/** Failed decode. `error` is safe to surface in UI: it never echoes the input. */
export interface NsecValidationError {
  readonly error: string;
}

export type NsecValidationResult = NsecValidationOk | NsecValidationError;

/** Type guard so callers can branch without prop existence checks. */
export function isNsecValidationOk(
  result: NsecValidationResult,
): result is NsecValidationOk {
  return 'pubkey' in result;
}

/**
 * Validate an `nsec1...` string and derive its pubkey.
 *
 * Returns either `{ pubkey, secretHex }` for a valid nsec or
 * `{ error }` for an invalid one. The error message NEVER echoes the
 * input so it is safe to render directly in the UI.
 */
export function validateNsec(rawInput: string): NsecValidationResult {
  if (typeof rawInput !== 'string') {
    return { error: 'Invalid nsec - expected a string starting with `nsec1`' };
  }
  const input = rawInput.trim();
  if (input.length === 0) {
    return { error: 'Enter your nsec to sign in' };
  }
  if (!input.startsWith('nsec1')) {
    return {
      error: 'Invalid nsec - should start with `nsec1` and be ~63 characters',
    };
  }

  let decoded: ReturnType<typeof nip19.decode>;
  try {
    decoded = nip19.decode(input);
  } catch {
    return {
      error: 'Invalid nsec - should start with `nsec1` and be ~63 characters',
    };
  }
  if (decoded.type !== 'nsec') {
    return {
      error: 'Invalid nsec - should start with `nsec1` and be ~63 characters',
    };
  }

  // nip19.decode for 'nsec' returns Uint8Array data per nostr-tools >=2.x.
  const data: unknown = decoded.data;
  if (!(data instanceof Uint8Array) || data.length !== 32) {
    return {
      error: 'Invalid nsec - should start with `nsec1` and be ~63 characters',
    };
  }

  let pubkey: string;
  let secretHex: string;
  try {
    pubkey = getPublicKey(data);
    secretHex = bytesToHexLocal(data);
  } catch {
    return {
      error: 'Invalid nsec - should start with `nsec1` and be ~63 characters',
    };
  }

  return { pubkey, secretHex };
}

/**
 * Build a `NostrSigner` backed by a hex-encoded secret key.
 *
 * Wire this into `publishToNostr()` for nsec-mode users; the kind
 * allowlist is still enforced because the chokepoint runs pre- and
 * post-sign checks.
 */
export function makeNsecSigner(secretHex: string): NostrSigner {
  const pubkey = derivePubkey(secretHex);
  return {
    type: 'nsec',
    pubkey,
    async signEvent(event: UnsignedNostrEvent): Promise<SignedNostrEvent> {
      return signWithNsec(event, secretHex);
    },
  };
}

/**
 * Sign an event with a raw secret key (hex). Throws `InvalidNsecError`
 * if the hex cannot be parsed. The error never includes the input.
 */
export function signWithNsec(
  event: UnsignedNostrEvent,
  secretHex: string,
): SignedNostrEvent {
  const bytes = parseSecretHexOrThrow(secretHex);
  const signed = finalizeEvent(
    {
      kind: event.kind,
      created_at: event.created_at,
      tags: event.tags,
      content: event.content,
    },
    bytes,
  ) as unknown as SignedNostrEvent;
  if (signed.kind !== event.kind) {
    throw new Error(
      `nsec signer returned kind ${signed.kind}, expected ${event.kind}`,
    );
  }
  return signed;
}

function derivePubkey(secretHex: string): string {
  const bytes = parseSecretHexOrThrow(secretHex);
  return getPublicKey(bytes);
}

function parseSecretHexOrThrow(secretHex: string): Uint8Array {
  if (typeof secretHex !== 'string' || !/^[0-9a-f]{64}$/i.test(secretHex)) {
    throw new InvalidNsecError();
  }
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    const byte = parseInt(secretHex.substr(i * 2, 2), 16);
    if (Number.isNaN(byte)) {
      throw new InvalidNsecError();
    }
    bytes[i] = byte;
  }
  return bytes;
}

function bytesToHexLocal(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    const v = bytes[i] ?? 0;
    out += v.toString(16).padStart(2, '0');
  }
  return out;
}
